import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  MAActionType,
  GetAccountFlowStatisticsSuccessAction,
  GetAccountFlowStatisticsFailAction,
  BootStrapAccountSuccessAction,
  BootStrapAccountFailAction,
  BootStrapAccountAction,
  GetCreatedFlowsByMonthsSuccessAction,
  GetCreatedFlowsByMonthsFailAction,
  GetCreatedFlowsByDaysSuccessAction,
  GetCreatedFlowsByDaysFailAction,
  GetActiveProfileInFlowsSuccessAction,
  GetActiveProfileInFlowsFailAction,
  GetActiveFlowsSuccessAction,
  GetActiveFlowsFailAction,
  GetActiveFlowsAction,
} from './ma.actions';
import { MaService } from '../services/ma.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class MAEffects {
  @Effect()
  getAccountFlowStatistics$ = this.actions$.pipe(
    ofType(MAActionType.GetAccountFlowStatistics),
    switchMap(() =>
      this.maService.getAccounts().pipe(
        map((response) => new GetAccountFlowStatisticsSuccessAction(response)),
        catchError((error) => {
          this.toastr.error(
            'Could not fetch accounts statistics',
            'Request failed',
          );
          return [new GetAccountFlowStatisticsFailAction(error)];
        }),
      ),
    ),
  );

  @Effect()
  bootstrapAccount$ = this.actions$.pipe(
    ofType(MAActionType.BootStrapAccount),
    switchMap(({ payload: { accounts } }: BootStrapAccountAction) =>
      this.maService.bootstrap({ accounts }).pipe(
        map((response) => {
          this.toastr.info(`Bootstrap completed`);
          return new BootStrapAccountSuccessAction(response);
        }),
        catchError((error) => {
          this.toastr.error('Could not bootstrap', 'Request failed');
          return [new BootStrapAccountFailAction(error)];
        }),
      ),
    ),
  );

  @Effect()
  getCreatedFlowsByMonths$ = this.actions$.pipe(
    ofType(MAActionType.GetCreatedFlowsByMonths),
    switchMap(() =>
      this.maService.getFlowStatistics({ type: 'flows', period: 'month' }).pipe(
        map((response) => {
          return new GetCreatedFlowsByMonthsSuccessAction(response);
        }),
        catchError((error) => {
          this.toastr.error('Could not get flow statistics', 'Request failed');
          return [new GetCreatedFlowsByMonthsFailAction(error)];
        }),
      ),
    ),
  );

  @Effect()
  getCreatedFlowsByDays$ = this.actions$.pipe(
    ofType(MAActionType.GetCreatedFlowsByDays),
    switchMap(() =>
      this.maService.getFlowStatistics({ type: 'flows', period: 'day' }).pipe(
        map((response) => {
          return new GetCreatedFlowsByDaysSuccessAction(response);
        }),
        catchError((error) => {
          this.toastr.error('Could not get flow statistics', 'Request failed');
          return [new GetCreatedFlowsByDaysFailAction(error)];
        }),
      ),
    ),
  );

  @Effect()
  getActiveProfilesInFlows$ = this.actions$.pipe(
    ofType(MAActionType.GetActiveProfileInFlows),
    switchMap(() =>
      this.maService
        .getFlowStatistics({ type: 'profiles', period: 'month' })
        .pipe(
          map((response) => {
            return new GetActiveProfileInFlowsSuccessAction(response);
          }),
          catchError((error) => {
            this.toastr.error(
              'Could not get flow statistics',
              'Request failed',
            );
            return [new GetActiveProfileInFlowsFailAction(error)];
          }),
        ),
    ),
  );

  @Effect()
  getActiveFlows$ = this.actions$.pipe(
    ofType(MAActionType.GetActiveFlows),
    switchMap((action: GetActiveFlowsAction) =>
      this.maService
        .getFlowStatistics({
          type: 'flows',
          report: 'active',
          length: action.payload,
        })
        .pipe(
          map((response) => {
            return new GetActiveFlowsSuccessAction(response);
          }),
          catchError((error) => {
            this.toastr.error(
              'Could not get active flow statistics',
              'Request failed',
            );
            return [new GetActiveFlowsFailAction(error)];
          }),
        ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly toastr: ToastrService,
    private readonly maService: MaService,
  ) {}
}
